import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import Sidebar from '../../components/Sidebar/Sidebar';
import { toast } from 'react-toastify';
import axios from 'axios';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faUser, 
  faCalendarAlt, 
  faTable, 
  faClock, 
  faEnvelope, 
  faPhone,
  faArrowLeft,
  faPrint,
  faPaperPlane,
  faReceipt,
  faCalculator,
  faTag,
  faCircleCheck,
  faCreditCard,
  faUtensils
} from '@fortawesome/free-solid-svg-icons';
import generatePrintInvoiceHTML from '../../utils/printInvoice';
import { buildApiUrl } from '../../config/api.config';

const InvoiceDetail = () => {
  const { invoiceId } = useParams();
  const navigate = useNavigate();
  const [state, setState] = useState({
    invoice: null,
    loading: true,
    error: null,
    isSending: false
  });

  useEffect(() => {
    const fetchInvoiceDetail = async () => {
      try {
        const uDetails = JSON.parse(localStorage.getItem("userDetails"));
        const selectedRestId = localStorage.getItem("currentRestaurantId");
        let activeRestaurantId = uDetails?.role === 'owner' ? selectedRestId : uDetails?.restaurantId;

        // Handle populated restaurantId object
        if (activeRestaurantId && typeof activeRestaurantId === 'object') {
            activeRestaurantId = activeRestaurantId._id;
        }

        const response = await axios.get(buildApiUrl(`invoice/admin/${invoiceId}?restaurantId=${activeRestaurantId}`), {
          headers: { 'auth-token': localStorage.getItem('token') }
        });
        setState(prev => ({
          ...prev,
          invoice: response.data,
          loading: false
        }));
      } catch (error) {
        console.error('Error fetching invoice details:', error);
        setState(prev => ({
          ...prev,
          loading: false,
          error: 'Failed to synchronize record details.'
        }));
      }
    };

    fetchInvoiceDetail();
  }, [invoiceId]);

  const sendInvoice = async () => {
    try {
      setState(prev => ({ ...prev, isSending: true }));
      await axios.post(
        buildApiUrl(`users/send-invoice/${invoiceId}`),
        { userId: state.invoice.userId._id }
      );
      toast.success('Digital invoice dispatched successfully');
      setState(prev => ({ ...prev, isSending: false }));
    } catch (error) {
      toast.error('Dispatch sequence failed');
      setState(prev => ({ ...prev, isSending: false }));
    }
  };

  const printInvoice = () => {
    const printWindow = window.open('', '', 'height=800,width=1200');
    const invoiceHTML = generatePrintInvoiceHTML(state.invoice);
    printWindow.document.write(invoiceHTML);
    printWindow.document.close();
    printWindow.print();
  };

  if (state.loading) {
    return (
      <div className="admin-layout">
        <Sidebar />
        <main>
           <div className="tf-flex-center" style={{ padding: '10rem 2rem', flexDirection: 'column', gap: '1.5rem' }}>
              <div className="tf-spinner"></div>
              <p style={{ color: 'var(--tf-text-muted)', fontWeight: '500' }}>Accessing financial record...</p>
           </div>
        </main>
      </div>
    );
  }

  if (state.error || !state.invoice) {
    return (
      <div className="admin-layout">
        <Sidebar />
        <main>
           <div className="tf-empty-state">
              <FontAwesomeIcon icon={faReceipt} size="3x" style={{ marginBottom: '1.5rem', opacity: 0.1 }} />
              <p>{state.error || 'Invoice record not found'}</p>
              <button className="tf-btn tf-btn-primary" style={{ marginTop: '1rem' }} onClick={() => navigate(-1)}>Return to Registry</button>
           </div>
        </main>
      </div>
    );
  }

  const invoice = state.invoice;
  const isPaid = invoice.status === 'paid';

  return (
    <div className="admin-layout">
      <Sidebar />
      <main>
        <div className="tf-card" style={{ marginBottom: '2rem' }}>
          <header style={{ marginBottom: '3rem' }}>
            <button 
              onClick={() => navigate(-1)} 
              className="tf-btn" 
              style={{ background: 'rgba(255,255,255,0.05)', color: 'var(--tf-text-muted)', padding: '0.5rem 1rem', fontSize: '0.85rem', marginBottom: '1.5rem', display: 'flex', alignItems: 'center', gap: '0.5rem' }}
            >
              <FontAwesomeIcon icon={faArrowLeft} /> Return to Inbox
            </button>
            
            <div className="tf-flex" style={{ justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '1.5rem' }}>
              <div className="tf-flex" style={{ gap: '1rem', alignItems: 'center' }}>
                <div style={{ width: '56px', height: '56px', borderRadius: '14px', background: 'linear-gradient(135deg, var(--tf-primary) 0%, #ff6b6b 100%)', color: 'white', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '1.4rem' }}>
                  <FontAwesomeIcon icon={faReceipt} />
                </div>
                <div>
                  <h1 className="tf-heading-md" style={{ margin: 0, fontSize: '1.8rem' }}>Master Ledger #{invoice.invoiceNumber}</h1>
                  <p style={{ color: 'var(--tf-text-muted)', margin: 0 }}>Complete audit view of customer transaction and settlement status</p>
                </div>
              </div>

              <div className="tf-flex" style={{ gap: '1rem' }}>
                <button type="button" onClick={printInvoice} className="tf-btn" style={{ padding: '0.8rem 1.5rem', background: 'rgba(255,255,255,0.05)', color: 'white', display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                  <FontAwesomeIcon icon={faPrint} /> Print Copy
                </button>
                {invoice.userId && (
                  <button type="button" onClick={sendInvoice} className="tf-btn tf-btn-primary" style={{ padding: '0.8rem 1.5rem', display: 'flex', alignItems: 'center', gap: '0.75rem' }} disabled={state.isSending}>
                    <FontAwesomeIcon icon={faPaperPlane} /> {state.isSending ? 'Dispatching...' : 'Dispatch Digital'}
                  </button>
                )}
              </div>
            </div>
          </header>

          <div className="tf-grid" style={{ gap: '2rem' }}>
            {/* Top Summaries */}
            <div className="tf-grid" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: '1.5rem' }}>
                <div className="tf-card" style={{ padding: '1.5rem', background: 'rgba(255,255,255,0.02)' }}>
                  <div className="tf-flex" style={{ gap: '0.75rem', marginBottom: '1rem' }}>
                    <FontAwesomeIcon icon={faCreditCard} style={{ color: 'var(--tf-text-muted)', opacity: 0.5 }} />
                    <span style={{ fontSize: '0.75rem', textTransform: 'uppercase', fontWeight: '700', color: 'var(--tf-text-muted)' }}>Transaction Baseline</span>
                  </div>
                  <div style={{ fontSize: '1.5rem', fontWeight: '800' }}>₹{(invoice.subtotal || 0).toFixed(2)}</div>
                </div>
                
                <div className="tf-card" style={{ padding: '1.5rem', background: 'rgba(255,255,255,0.02)', borderLeft: '3px solid var(--tf-error)' }}>
                  <div className="tf-flex" style={{ gap: '0.75rem', marginBottom: '1rem' }}>
                    <FontAwesomeIcon icon={faTag} style={{ color: 'var(--tf-error)', opacity: 0.5 }} />
                    <span style={{ fontSize: '0.75rem', textTransform: 'uppercase', fontWeight: '700', color: 'var(--tf-error)' }}>Applied Deductions</span>
                  </div>
                  <div style={{ fontSize: '1.5rem', fontWeight: '800', color: 'var(--tf-error)' }}>-₹{invoice.discount.toFixed(2)}</div>
                </div>

                <div className="tf-card" style={{ padding: '1.5rem', background: 'rgba(255,255,255,0.02)' }}>
                  <div className="tf-flex" style={{ gap: '0.75rem', marginBottom: '1rem' }}>
                    <FontAwesomeIcon icon={faCalculator} style={{ color: 'var(--tf-text-muted)', opacity: 0.5 }} />
                    <span style={{ fontSize: '0.75rem', textTransform: 'uppercase', fontWeight: '700', color: 'var(--tf-text-muted)' }}>Aggregated Taxes</span>
                  </div>
                  <div style={{ fontSize: '1.5rem', fontWeight: '800' }}>₹{(invoice.cgst + invoice.sgst).toFixed(2)}</div>
                </div>

                <div className="tf-card" style={{ padding: '2rem', background: 'var(--tf-primary)', color: 'white', position: 'relative', overflow: 'hidden', boxShadow: '0 10px 30px rgba(255, 65, 53, 0.2)' }}>
                  <FontAwesomeIcon icon={isPaid ? faCircleCheck : faReceipt} style={{ position: 'absolute', right: '-15px', bottom: '-15px', fontSize: '6rem', opacity: 0.15 }} />
                  <div style={{ fontSize: '0.8rem', textTransform: 'uppercase', fontWeight: '800', marginBottom: '1rem', color: 'rgba(255,255,255,0.8)', letterSpacing: '1px' }}>
                    {isPaid ? 'Final Settlement (Paid)' : 'Total Due (Unpaid)'}
                  </div>
                  <div style={{ fontSize: '2.2rem', fontWeight: '900' }}>₹{(invoice.finalAmount || invoice.totalAmount).toFixed(2)}</div>
                </div>
            </div>

            {/* Content Body */}
            <div className="tf-grid" style={{ gridTemplateColumns: '1fr 340px', gap: '2.5rem', alignItems: 'flex-start' }}>
               <div className="tf-grid" style={{ gap: '2rem' }}>
                  {/* Items list */}
                  <div className="tf-card" style={{ padding: '2rem' }}>
                    <h2 className="tf-heading-sm tf-flex" style={{ gap: '0.75rem', marginBottom: '2rem' }}>
                      <FontAwesomeIcon icon={faUtensils} style={{ color: 'var(--tf-primary)' }} />
                      Audited Particulars
                    </h2>
                    <div className="tf-list-container">
                      <div className="tf-list-header" style={{ gridTemplateColumns: '60px 2fr 1fr 1fr 140px', gap: '1.5rem', padding: '1.25rem 1.5rem' }}>
                        <span>IDX</span>
                        <span>MENU NOMENCLATURE</span>
                        <span style={{ textAlign: 'right' }}>BASE RATE</span>
                        <span style={{ textAlign: 'center' }}>QTY</span>
                        <span style={{ textAlign: 'right' }}>NET SUM</span>
                      </div>
                      <div className="tf-grid" style={{ gap: '0' }}>
                        {invoice.foods.map((food, index) => (
                          <div key={index} className="tf-list-item" style={{ gridTemplateColumns: '60px 2fr 1fr 1fr 140px', gap: '1.5rem', padding: '1.25rem 1.5rem' }}>
                            <div style={{ color: 'var(--tf-text-muted)', fontSize: '0.85rem' }}>{String(index + 1).padStart(2, '0')}</div>
                            <div style={{ fontWeight: '600' }}>{food.name}</div>
                            <div style={{ textAlign: 'right', color: 'var(--tf-text-muted)' }}>₹{(food.price || 0).toFixed(2)}</div>
                            <div style={{ textAlign: 'center', fontWeight: '700' }}>{food.quantity}</div>
                            <div style={{ textAlign: 'right', fontWeight: '700', color: 'var(--tf-primary)' }}>₹{(food.quantity * food.price).toFixed(2)}</div>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
               </div>

               {/* Right Side panel */}
               <div className="tf-grid" style={{ gap: '2rem' }}>
                  {/* Customer Info */}
                  <div className="tf-card" style={{ padding: '1.5rem' }}>
                    <h3 className="tf-text-accent" style={{ fontSize: '0.9rem', marginBottom: '1.5rem', display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                       <FontAwesomeIcon icon={faUser} />
                       Customer Identity
                    </h3>
                    <div className="tf-grid" style={{ gap: '1.25rem' }}>
                       <div className="tf-flex" style={{ gap: '1rem' }}>
                          <div style={{ padding: '0.75rem', borderRadius: '8px', background: 'rgba(255,255,255,0.03)', color: 'var(--tf-primary)' }}><FontAwesomeIcon icon={faUser} /></div>
                          <div>
                            <div style={{ fontSize: '0.7rem', textTransform: 'uppercase', color: 'var(--tf-text-muted)', fontWeight: '700' }}>Full Name</div>
                            <div style={{ fontWeight: '600', fontSize: '1rem' }}>{invoice.userId?.name || 'Walk-in Guest'}</div>
                          </div>
                       </div>
                       <div className="tf-flex" style={{ gap: '1rem' }}>
                          <div style={{ padding: '0.75rem', borderRadius: '8px', background: 'rgba(255,255,255,0.03)', color: 'var(--tf-text-muted)' }}><FontAwesomeIcon icon={faEnvelope} /></div>
                          <div>
                            <div style={{ fontSize: '0.7rem', textTransform: 'uppercase', color: 'var(--tf-text-muted)', fontWeight: '700' }}>Email Contact</div>
                            <div style={{ wordBreak: 'break-all', fontSize: '0.9rem' }}>{invoice.userId?.email || 'N/A'}</div>
                          </div>
                       </div>
                       <div className="tf-flex" style={{ gap: '1rem' }}>
                          <div style={{ padding: '0.75rem', borderRadius: '8px', background: 'rgba(255,255,255,0.03)', color: 'var(--tf-text-muted)' }}><FontAwesomeIcon icon={faPhone} /></div>
                          <div>
                            <div style={{ fontSize: '0.7rem', textTransform: 'uppercase', color: 'var(--tf-text-muted)', fontWeight: '700' }}>Mobile Contact</div>
                            <div style={{ fontSize: '0.9rem' }}>{invoice.userId?.contact || 'N/A'}</div>
                          </div>
                       </div>
                    </div>
                  </div>

                  {/* Reservation info */}
                  {(() => {
                    const rti = invoice.reservedTableInfo;
                    if (!rti) return null;
                    
                    if (Array.isArray(rti) && rti.length === 0) return null;
                    const reservations = Array.isArray(rti) ? rti : [rti];
                    
                    return reservations.map((resInfo, idx) => (
                      <div key={idx} className="tf-card" style={{ padding: '1.5rem', border: '1px solid var(--tf-success)', background: 'rgba(16, 185, 129, 0.02)', marginTop: idx > 0 ? '1.5rem' : 0 }}>
                        <h3 style={{ fontSize: '0.9rem', color: 'var(--tf-success)', marginBottom: '1.5rem', display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                           <FontAwesomeIcon icon={faTable} />
                           Allocated Reservation {reservations.length > 1 ? `#${idx + 1}` : ''}
                        </h3>
                        <div className="tf-grid" style={{ gap: '1.25rem' }}>
                           <div className="tf-flex" style={{ gap: '1rem' }}>
                              <div style={{ color: 'var(--tf-success)' }}><FontAwesomeIcon icon={faTable} /></div>
                              <div>
                                 <div style={{ fontSize: '0.7rem', color: 'var(--tf-text-muted)' }}>Table Number</div>
                                 <div style={{ fontWeight: '700' }}>{resInfo.tableNumber || 'N/A'}</div>
                              </div>
                           </div>
                           <div className="tf-flex" style={{ gap: '1rem' }}>
                              <div style={{ color: 'var(--tf-text-muted)' }}><FontAwesomeIcon icon={faClock} /></div>
                              <div>
                                 <div style={{ fontSize: '0.7rem', color: 'var(--tf-text-muted)' }}>Time Interval</div>
                                 <div style={{ fontSize: '0.9rem' }}>{resInfo.slotTime || 'N/A'}</div>
                              </div>
                           </div>
                           <div className="tf-flex" style={{ gap: '1rem' }}>
                              <div style={{ color: 'var(--tf-text-muted)' }}><FontAwesomeIcon icon={faCalendarAlt} /></div>
                              <div>
                                 <div style={{ fontSize: '0.7rem', color: 'var(--tf-text-muted)' }}>Event Date</div>
                                 <div style={{ fontSize: '0.9rem' }}>{resInfo.date ? new Date(resInfo.date).toLocaleDateString(undefined, { month: 'long', day: 'numeric', year: 'numeric' }) : 'N/A'}</div>
                              </div>
                           </div>
                        </div>
                      </div>
                    ));
                  })()}
               </div>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
};

export default InvoiceDetail;